import React, { useState, useRef } from "react";
import Export from "./components/Export";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSalesData } from "./hooks/useSalesData";
import { getMonthOptions } from "./hooks/getMonthOptions";
import { formatSales } from "./hooks/formatSales";
import { convertToCSV } from "./hooks/convertToCSV";
import printChart from "../../../../PrintChart";
import StoreFilter from "./components/StoreFilter";
import MonthFilter from "./components/MonthFilter";
import YearFilter from "./components/YearFilter";
import InitialPrompt from "../../../../components/InitialPrompt";
import LoadingState from "../../../../components/LoadingState";

function MonthlySalesChartPage({
  objects,
  onChangeDate,
  onChangeStore,
  store,
  storess,
  progress,
}) {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [range, setRange] = useState("");
  const [selectedMonthName, setSelectedMonthName] = useState("");
  const chartRef = useRef(null);
  const salesData = useSalesData(objects);
  const months = getMonthOptions(selectedYear);
  const chartLabels = Object.keys(salesData).reverse();
  const chartSalesData = Object.values(salesData).map((data) => data.sales);

  const data = {
    labels: chartLabels,
    datasets: [
      {
        type: "bar",
        label: "Monthly Sales",
        data: chartSalesData,
        backgroundColor: ["#006096"],
        borderColor: ["#006096"],
        borderWidth: 1,
      },
    ],
  };

  const highchartsOptions = {
    chart: { type: "column" },
    title: { text: "Total Gross Sales" },
    xAxis: { categories: ["Total Gross Sales"] },
    yAxis: {
      title: { text: "Gross Sales (PHP)" },
      labels: {
        formatter() {
          return "₱" + this.value.toLocaleString();
        },
      },
    },
    tooltip: { valuePrefix: "₱", valueDecimals: 2 },
    series: [
      {
        name: "Gross Sales",
        data: [chartSalesData.reduce((a, b) => a + b, 0)],
        color: "#006096",
      },
    ],
    credits: { enabled: false },
  };

  const handleMonthChange = (event) => {
    const monthNames = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };
    let monthName;
    setSelectedMonth(event.target.value);

    if (event.target.value === "AllMonths") {
      onChangeDate("AllMonths");
    } else {
      setRange(event.target.value);
      const cleanedValue = event.target.value.replace(/-/g, "");
      const parsedValue = JSON.parse(cleanedValue);
      onChangeDate(parsedValue);
      const monthi = JSON.parse(event.target.value).$gte.split("-");
      monthName = Object.keys(monthNames).find(
        (key) => monthNames[key] === monthi[1]
      );
    }

    setSelectedMonthName(monthName);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    const parsedRange = JSON.parse(range);
    parsedRange.$gte =
      year + parsedRange.$gte.substring(parsedRange.$gte.indexOf("-"));
    parsedRange.$lte =
      year + parsedRange.$lte.substring(parsedRange.$lte.indexOf("-"));
    setSelectedYear(year);
    onChangeDate(parsedRange);
  };

  const exportToCSV = () => {
    const csvData = chartLabels.map((monthYear, i) => ({
      brand: "Angels Pizza",
      store: storess,
      monthYear,
      sales: chartSalesData[i],
    }));
    const csvString = convertToCSV(csvData);
    const a = document.createElement("a");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = "monthly-sales-data.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h2 className="fw-bold text-capitalize">
          <span style={{ color: "darkcolor" }}>Monthly Gross</span>
          <span style={{ color: "#83c14a" }}> Sales Chart</span>
        </h2>
        <Export
          exportCsv={exportToCSV}
          exportPdf={() => printChart("chart-container")}
        />
      </div>
      <div className="row mb-2">
        <StoreFilter
          store={store}
          storess={storess}
          onChangeStore={onChangeStore}
        />
        <MonthFilter
          months={months}
          selectedMonth={selectedMonth}
          selectedMonthName={selectedMonthName}
          handleMonthChange={handleMonthChange}
        />
        <YearFilter
          selectedYear={selectedYear}
          handleYearChange={handleYearChange}
        />
      </div>

      {!storess && !selectedYear && <InitialPrompt />}

      {!progress &&
        objects?.length > 0 &&
        storess &&
        selectedMonthName &&
        selectedYear && (
          <div ref={chartRef} id="chart-container">
            <div className="card">
              <HighchartsReact
                highcharts={Highcharts}
                options={highchartsOptions}
              />
            </div>
            <div className="print-only-content">
              <h2>Report Details</h2>
              <div className="d-flex justify-content-between align-items-center print-only-content">
                <h3>Brand: Angels Pizza</h3>
                <h4 className="mx-auto"></h4>
                <h3>Store: {storess}</h3>
              </div>
              <div className="print-only" style={{ fontSize: "20px" }}>
                <table className="table text-center">
                  <thead>
                    <tr className="d-flex">
                      <th>Month</th>
                      <th className="ms-5">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.labels.map((label, index) => (
                      <tr className="d-flex" key={label}>
                        <td>{label}</td>
                        <td>{formatSales(data.datasets[0].data[index])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      {objects?.length === 0 &&
        storess &&
        selectedMonthName &&
        selectedYear && (
          <div className="text-center mt-5">
            <LoadingState />
          </div>
        )}
    </>
  );
}

export default MonthlySalesChartPage;
